import React from "react";
import { Camera } from "lucide-react";

/**
 * Clean, neutral placeholder shown wherever a listing has no photo yet.
 * Used in place of generic stock-photo fallbacks.
 */
export default function PhotoPlaceholder({ className = "", iconClassName = "" }) {
  return (
    <div className={`bg-slate-50 flex items-center justify-center ${className}`} aria-label="No photo provided">
      <div className="flex flex-col items-center gap-2 text-slate-300">
        <Camera className={`w-10 h-10 ${iconClassName}`} strokeWidth={1.5} />
        <span className="text-xs font-bold tracking-wider uppercase">No photo</span>
      </div>
    </div>
  );
}

/**
 * Drop-in replacement for <img src={photo} ... /> that shows
 * PhotoPlaceholder when the URL is missing OR fails to load.
 *
 * Iteration 304 — performance: animated skeleton pulse while the image
 * hydrates, async decoding, and an `eager` flag that sets
 * loading="eager" + fetchPriority="high" for above-the-fold grid cards
 * (everything else lazy-loads so the first page renders instantly).
 */
export function ListingPhoto({ src, alt = "", className = "", testId, eager = false, blur = "" }) {
  const [failed, setFailed] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  if (!src || failed) return <PhotoPlaceholder className={className} />;
  return (
    <img
      src={src}
      alt={alt}
      className={`${className} transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0 animate-pulse bg-slate-100"}`}
      style={blur ? { backgroundImage: `url(${blur})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
      loading={eager ? "eager" : "lazy"}
      fetchPriority={eager ? "high" : "auto"}
      decoding="async"
      onLoad={() => setLoaded(true)}
      onError={() => setFailed(true)}
      data-testid={testId}
      data-eager={eager ? "true" : undefined}
    />
  );
}
